import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Stack,
  Switch,
  Table,
  Text,
  Title,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlayerStop,
  IconPower,
  IconServer,
} from "@tabler/icons-react";
import {
  apiDelete,
  apiFetch,
  apiPost,
  friendlyError,
  type ApiError,
} from "../../api/client.js";
import { useAuth } from "../../auth/AuthContext.js";
import { StatusPill } from "../../components/StatusPill.js";
import { CreateVmModal } from "./CreateVmModal.js";
import { backupActionError, isApiError } from "./BackupsCard.js";
import {
  formatBytes,
  formatRam,
  formatUptime,
  vmAction,
  type VmAction,
  type VmListItem,
} from "./vmUtils.js";

const POLL_MS = 5000;

export function Dashboard() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [vms, setVms] = useState<VmListItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pveDown, setPveDown] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState<Set<number>>(new Set());
  const [createOpened, setCreateOpened] = useState(false);

  async function load() {
    try {
      const data = await apiFetch<VmListItem[]>("/api/vms");
      setVms(data);
      setError(null);
      setPveDown(false);
      setLastUpdated(Date.now());
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 502 && apiErr.error === "pve_unavailable") {
        // PVE unreachable — keep showing last known VMs and surface a
        // retrying banner. Do NOT mark rows orphaned (that's "VM gone",
        // a different signal). The 5s poll retries automatically.
        setPveDown(true);
        setError(null);
      } else {
        setPveDown(false);
        setError(friendlyError(err, "Could not load VMs."));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1s ticker so the "Last updated Xs ago" label feels live between polls.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  function setRowBusy(vmid: number, on: boolean) {
    setBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(vmid);
      else next.delete(vmid);
      return next;
    });
  }

  async function runAction(vm: VmListItem, action: VmAction, body?: { force?: boolean; timeout?: number }) {
    if (vm.vmid === null) return;
    setRowBusy(vm.vmid, true);
    try {
      const res = await vmAction(vm.vmid, action, body);
      notifications.show({ color: res.ok ? "green" : "red", message: res.message });
      if (res.ok) void load();
    } finally {
      setRowBusy(vm.vmid, false);
    }
  }

  function confirmPower(vm: VmListItem, action: "shutdown" | "stop") {
    if (vm.vmid === null) return;
    const isStop = action === "stop";
    modals.openConfirmModal({
      title: isStop ? "Force stop VM" : "Shut down VM",
      centered: true,
      children: (
        <Text size="sm">
          {isStop ? (
            <>
              Force stop <b>{vm.name}</b>? This pulls the power and may corrupt the filesystem.
            </>
          ) : (
            <>
              Shut down <b>{vm.name}</b>? An ACPI shutdown is sent; the guest may take a while.
            </>
          )}
        </Text>
      ),
      labels: { confirm: isStop ? "Force stop" : "Shut down", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => void runAction(vm, action, isStop ? undefined : { force: false }),
    });
  }

  function onBackupSwitchChange(vm: VmListItem) {
    if (vm.vmid === null) return;
    if (!vm.backupEnabled) {
      void enableRowBackups(vm.vmid);
    } else {
      confirmRemoveBackups(vm.vmid, vm.name);
    }
  }

  async function enableRowBackups(vmid: number) {
    setRowBusy(vmid, true);
    try {
      try {
        const res = await apiPost<{ schedule: string }>(`/api/vms/${vmid}/backups`, {
          hour: 2,
          minute: 0,
        });
        notifications.show({
          color: "green",
          message: `Backups enabled — daily at ${res.schedule}`,
        });
      } catch (err) {
        if (isApiError(err) && err.status === 409 && err.error === "backup_job_exists") {
          // Another tab/poll enabled it first — reload; end state is the same.
        } else if (!isApiError(err)) {
          notifications.show({ color: "red", message: "Couldn't reach the server." });
          return;
        } else {
          const mapped = backupActionError(err, "Couldn't enable backups.");
          notifications.show({ color: "red", title: mapped.title, message: mapped.message });
          return;
        }
      }
      void load();
    } finally {
      setRowBusy(vmid, false);
    }
  }

  function confirmRemoveBackups(vmid: number, name: string) {
    modals.openConfirmModal({
      title: "Remove from backups",
      centered: true,
      children: (
        <Text size="sm">
          Daily backups stop for <b>{name}</b> — existing backups on the backup storage are
          kept. If it is the last server in the backup job, the whole job is deleted;
          re-enabling later starts a fresh job with the default 02:00 daily schedule and
          default retention.
        </Text>
      ),
      labels: { confirm: "Remove", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => void disableRowBackups(vmid),
    });
  }

  async function disableRowBackups(vmid: number) {
    setRowBusy(vmid, true);
    try {
      try {
        await apiDelete(`/api/vms/${vmid}/backups`);
      } catch (err) {
        if (!(isApiError(err) && err.status === 404 && err.error === "backup_job_not_found")) {
          notifications.show({
            color: "red",
            message: friendlyError(err, "Couldn't remove this server from backups."),
          });
          return;
        }
        // Already gone — same end state as a successful removal.
      }
      notifications.show({ color: "green", message: "Backups disabled" });
      void load();
    } finally {
      setRowBusy(vmid, false);
    }
  }

  const agoSec = lastUpdated !== null ? Math.max(0, Math.floor((now - lastUpdated) / 1000)) : null;
  const vmCount = vms?.length ?? 0;

  return (
    <Box>
      <Group justify="space-between" mb="lg" align="flex-end" wrap="nowrap">
        <Stack gap={4}>
          <Title order={2}>Virtual Machines</Title>
          <Text size="sm" c="dimmed">
            {loading
              ? "Loading your VMs…"
              : vmCount === 0
                ? "No VMs yet — deploy your first server."
                : `${vmCount} VM${vmCount === 1 ? "" : "s"}${
                    auth.session?.display_name ? ` for ${auth.session.display_name}` : ""
                  }`}
          </Text>
        </Stack>
        <Group gap="xs" wrap="nowrap">
          {agoSec !== null && (
            <Text size="xs" c="dimmed" visibleFrom="sm">
              Updated {agoSec}s ago
            </Text>
          )}
          <Button variant="default" size="sm" onClick={() => void load()} loading={loading}>
            Refresh
          </Button>
          <Button size="sm" leftSection={<IconServer size={16} stroke={1.75} />} onClick={() => setCreateOpened(true)}>
            New VM
          </Button>
        </Group>
      </Group>

      <CreateVmModal opened={createOpened} onClose={() => setCreateOpened(false)} onCreated={() => void load()} />

      {error && (
        <Alert color="red" variant="light" mb="md" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}
      {pveDown && (
        <Alert color="orange" variant="light" mb="md" title="Proxmox unreachable">
          Can&apos;t reach Proxmox right now — retrying in the background. Showing last known state.
        </Alert>
      )}

      {loading && vms === null ? (
        <Stack gap="xs" align="center" py="xl">
          <Loader />
          <Text size="sm" c="dimmed">
            Loading your VMs…
          </Text>
        </Stack>
      ) : vmCount === 0 ? (
        <EmptyState onCreate={() => setCreateOpened(true)} />
      ) : (
        <Table.ScrollContainer minWidth={700}>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>CPU</Table.Th>
                <Table.Th>RAM</Table.Th>
                <Table.Th>Disk</Table.Th>
                <Table.Th>Uptime</Table.Th>
                <Table.Th w={90}>Backups</Table.Th>
                <Table.Th w={110}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {vms?.map((vm) => {
                const isPending = vm.vmid === null;
                const isBusy = vm.vmid !== null && busy.has(vm.vmid);
                const rowKey = isPending ? `creation-${vm.creation_id ?? vm.name}` : `vm-${vm.vmid}`;
                const backupTooltip =
                  vm.backupEnabled && vm.backupPaused
                    ? "Backups paused by operator"
                    : !vm.backupOffered
                      ? "Backups not available — contact support"
                      : vm.backupEnabled
                        ? "Backups enabled — manage on the Backups card"
                        : "Enable daily backups (02:00 default)";
                return (
                  <Table.Tr
                    key={rowKey}
                    style={{
                      cursor: isPending ? "default" : "pointer",
                    }}
                    onClick={isPending ? undefined : () => navigate(`/dashboard/vms/${vm.vmid}`)}
                    className="vm-row"
                  >
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <ThemeIcon
                          size="sm"
                          variant="light"
                          color="brand"
                          radius="md"
                          style={{ flex: "0 0 auto" }}
                        >
                          <IconServer size={15} stroke={1.75} />
                        </ThemeIcon>
                        <Stack gap={0}>
                          <Anchor
                            component="button"
                            type="button"
                            size="sm"
                            fw={600}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              if (!isPending) navigate(`/dashboard/vms/${vm.vmid}`);
                            }}
                            style={{ textAlign: "left" }}
                          >
                            {vm.name}
                          </Anchor>
                          {isPending && (
                            <Text size="xs" c="dimmed">
                              Provisioning…
                            </Text>
                          )}
                        </Stack>
                        {isPending && <Loader size="xs" />}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <StatusPill status={vm.status} />
                    </Table.Td>
                    <Table.Td>{isPending ? "—" : vm.cpu_cores || "—"}</Table.Td>
                    <Table.Td>{isPending || !vm.memory_mb ? "—" : formatRam(vm.memory_mb)}</Table.Td>
                    <Table.Td>
                      {isPending
                        ? `${formatBytes(vm.disk_total_bytes)} req.`
                        : formatBytes(vm.disk_total_bytes)}
                    </Table.Td>
                    <Table.Td>{isPending ? "—" : formatUptime(vm.uptime_seconds)}</Table.Td>
                    <Table.Td>
                      {vm.vmid !== null && (
                        <Tooltip label={backupTooltip} position="bottom" withArrow>
                          <span onClick={(e) => e.stopPropagation()}>
                            <Switch
                              size="sm"
                              checked={vm.backupEnabled}
                              disabled={
                                !vm.backupOffered ||
                                (vm.backupPaused && vm.backupEnabled) ||
                                isBusy
                              }
                              onChange={() => onBackupSwitchChange(vm)}
                              aria-label={backupTooltip}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" onClick={(e) => e.stopPropagation()} wrap="nowrap">
                        {isPending ? (
                          <Text size="xs" c="dimmed">
                            creating…
                          </Text>
                        ) : (
                          <>
                            {vm.status === "running" && (
                              <>
                                <Tooltip label="Shutdown" position="bottom" withArrow>
                                  <ActionIcon
                                    size="md"
                                    variant="default"
                                    disabled={isBusy}
                                    onClick={() => confirmPower(vm, "shutdown")}
                                    aria-label="Shutdown"
                                  >
                                    <IconPower size={15} stroke={1.75} />
                                  </ActionIcon>
                                </Tooltip>
                                <Tooltip label="Stop (force)" position="bottom" withArrow>
                                  <ActionIcon
                                    size="md"
                                    variant="light"
                                    color="red"
                                    disabled={isBusy}
                                    onClick={() => confirmPower(vm, "stop")}
                                    aria-label="Stop (force)"
                                  >
                                    <IconPlayerStop size={15} stroke={1.75} />
                                  </ActionIcon>
                                </Tooltip>
                              </>
                            )}
                            {vm.status === "stopped" && (
                              <Tooltip label="Start" position="bottom" withArrow>
                                <ActionIcon
                                  size="md"
                                  variant="default"
                                  disabled={isBusy}
                                  onClick={() => void runAction(vm, "start")}
                                  aria-label="Start"
                                >
                                  <IconPlayerPlay size={15} stroke={1.75} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            {vm.status === "paused" && (
                              <Tooltip label="Resume is not supported yet" position="bottom" withArrow>
                                <ActionIcon
                                  size="sm"
                                  variant="default"
                                  disabled
                                  aria-label="Resume is not supported yet"
                                >
                                  <IconPlayerPause size={15} stroke={1.75} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            {(vm.status === "orphaned" ||
                              (vm.status !== "running" &&
                                vm.status !== "stopped" &&
                                vm.status !== "paused")) && (
                              <Text size="xs" c="dimmed">
                                —
                              </Text>
                            )}
                          </>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Box>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <Stack align="center" justify="center" gap="md" py={64} px="md" ta="center">
      <ThemeIcon size={56} variant="light" color="brand" radius="xl">
        <IconServer size={28} stroke={1.5} />
      </ThemeIcon>
      <Stack gap={4} align="center">
        <Title order={3}>No virtual machines yet</Title>
        <Text size="sm" c="dimmed" maw={420}>
          Deploy your first VM to get started — choose CPU, memory, disk, and an optional ISO.
        </Text>
      </Stack>
      <Button leftSection={<IconServer size={16} stroke={1.75} />} onClick={onCreate}>
        Deploy VM
      </Button>
    </Stack>
  );
}
